import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { NavBadge } from "@/components/NavBadge";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { useNavBadges } from "@/hooks/useNavBadges";
import { ApiError } from "@/lib/apiClient";
import { useCurrentUser, userIsBuero } from "@/lib/auth";
import {
  activateHeroDokument,
  canOpenDokumentZahlungen,
  DOKUMENT_TYP_LABELS,
  getDokument,
  listAngeboteNachfass,
  listDokumenteWithTotal,
  isHeroCustomerDocumentListItem,
  isStuecklisteListItem,
  isStundennachweisListItem,
  saveDocumentColumnSettings,
  type AngebotNachfassGroup,
  type DokumentDetail,
  type DokumentListItem,
  type DokumentStatus,
  type DokumentTyp,
} from "@/lib/dokumente";
import { toast } from "@/lib/toast";
import { useRealtimeSync } from "@/lib/useRealtime";
import { useTimesheetFlow } from "@/features/timesheetFlow/useTimesheetFlow";
import { useProjectOverview } from "@/projectOverview/useProjectOverview";
import { CollapsibleSection } from "@/components/ui/CollapsibleSection";
import { AngebotAgingPanels } from "@/pages/dokumente/AngebotAgingPanels";
import { AngebotNachfassModal } from "@/pages/dokumente/AngebotNachfassModal";
import { DocumentColumnsDialog } from "@/pages/dokumente/DocumentColumnsDialog";
import { MahnungDokumenteList } from "@/pages/dokumente/MahnungDokumenteList";
import { DocumentPaymentsDialog } from "@/pages/dokumente/DocumentPaymentsDialog";
import { useDocumentCreate } from "@/pages/dokumente/useDocumentCreate";
import { useDocumentEditor } from "@/pages/dokumente/useDocumentEditor";
import {
  DocumentFilterBar,
  documentHasActiveFilters,
  type DocumentFilters,
} from "@/pages/dokumente/DocumentFilterBar";
import { DocumentTable } from "@/pages/dokumente/DocumentTable";
import {
  clampDocumentColumnWidth,
  ensureRechnungPaymentColumn,
  type DocumentColumnKey,
} from "@/pages/dokumente/documentColumns";
import {
  getVisibleDocumentColumns,
  getVisibleDocumentWidths,
  useDocumentTableSettings,
  useMediaQuery,
} from "@/pages/dokumente/useDocumentTableSettings";
import { SearchInput } from "@/pages/projekte/SearchInput";
import { TableSortResetBar, useTableSort } from "@/components/dataTable";

const PAGE_SIZE = 100;

type DokumenteFirstPageCacheEntry = {
  items: DokumentListItem[];
  totalCount: number | null;
};

// Stale-while-revalidate: beim erneuten Öffnen sofort die letzte erste Seite
// zeigen (kein Vollbild-Spinner) und im Hintergrund frisch nachladen.
const _dokumenteFirstPageCache = new Map<string, DokumenteFirstPageCacheEntry>();

const DOCUMENT_STATUS_VALUES: DokumentStatus[] = [
  "entwurf",
  "erstellt",
  "versendet",
  "storniert",
  "angenommen",
  "abgelehnt",
  "abgerechnet",
  "geliefert",
];

const DEFAULT_FILTERS: DocumentFilters = {
  typ: "",
  status: "",
  datumVon: "",
  datumBis: "",
  q: "",
};

function parseDocumentStatusParam(value: string | null): DokumentStatus | "" {
  const raw = (value || "").trim().toLowerCase();
  return DOCUMENT_STATUS_VALUES.includes(raw as DokumentStatus) ? (raw as DokumentStatus) : "";
}

function formatDocumentCountLabel(count: number): string {
  if (count === 1) return "· 1 Dokument";
  return `· ${count} Dokumente`;
}

const LOCKED_PAGE: Record<
  DokumentTyp,
  { title: string; subtitle: string; createLabel: string }
> = {
  angebot: {
    title: "Angebote",
    subtitle: "Erstellte Angebote an Kunden",
    createLabel: "Neues Angebot",
  },
  rechnung: {
    title: "Rechnungen",
    subtitle: "Ausgangsrechnungen",
    createLabel: "Neue Rechnung",
  },
  lieferschein: {
    title: "Lieferscheine",
    subtitle: DOKUMENT_TYP_LABELS.lieferschein,
    createLabel: "Neuer Lieferschein",
  },
  auftragsbestaetigung: {
    title: "Auftragsbestätigungen",
    subtitle: DOKUMENT_TYP_LABELS.auftragsbestaetigung,
    createLabel: "Neue Auftragsbestätigung",
  },
  stundennachweis: {
    title: "Stundennachweise",
    subtitle: DOKUMENT_TYP_LABELS.stundennachweis,
    createLabel: "Neuer Stundennachweis",
  },
  stueckliste: {
    title: "Stücklisten",
    subtitle: DOKUMENT_TYP_LABELS.stueckliste,
    createLabel: "Neue Stückliste",
  },
};

export function DokumenteIndexPage({ lockedTyp }: { lockedTyp?: DokumentTyp } = {}) {
  const { open: openDocumentCreate } = useDocumentCreate();
  const { open: openDocumentEditor, isOpen: documentEditorOpen } = useDocumentEditor();
  const { openDetail: openTimesheetDetail } = useTimesheetFlow();
  const { open: openProjectOverview } = useProjectOverview();
  const { sort, toggle, reset: resetSort } = useTableSort();
  const { user } = useCurrentUser();
  const badgeCounts = useNavBadges(user);
  const [searchParams, setSearchParams] = useSearchParams();
  const showAngebotNachfass = userIsBuero(user);

  const [items, setItems] = useState<DokumentListItem[]>([]);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<DocumentFilters>(() => ({
    ...DEFAULT_FILTERS,
    typ: lockedTyp ?? "",
    status: parseDocumentStatusParam(searchParams.get("status")),
  }));
  const [columnsDialogOpen, setColumnsDialogOpen] = useState(false);
  const [paymentsDoc, setPaymentsDoc] = useState<DokumentDetail | null>(null);
  const [paymentBusyId, setPaymentBusyId] = useState<number | null>(null);
  const [heroOpenBusyId, setHeroOpenBusyId] = useState<number | null>(null);
  const [nachfassGroups, setNachfassGroups] = useState<AngebotNachfassGroup[]>([]);
  const [nachfassFollowedGroups, setNachfassFollowedGroups] = useState<AngebotNachfassGroup[]>([]);
  const [nachfassPendingGroups, setNachfassPendingGroups] = useState<AngebotNachfassGroup[]>([]);
  const [nachfassLoading, setNachfassLoading] = useState(false);
  const [nachfassGroup, setNachfassGroup] = useState<AngebotNachfassGroup | null>(null);
  const { settings, setSettings, saveSettings } = useDocumentTableSettings();
  const isMobile = useMediaQuery("(max-width: 767px)");
  const isMountedRef = useRef(true);
  const itemsLengthRef = useRef(0);
  const hasLoadedOnceRef = useRef(false);
  const pendingSettingsRef = useRef<typeof settings | null>(null);
  const widthSaveTimerRef = useRef<number | null>(null);
  itemsLengthRef.current = items.length;

  const statusParam = searchParams.get("status");
  useEffect(() => {
    hasLoadedOnceRef.current = false;
    setFilters({
      ...DEFAULT_FILTERS,
      typ: lockedTyp ?? "",
      status: parseDocumentStatusParam(statusParam),
    });
  }, [lockedTyp, statusParam]);

  const visibleColumns = useMemo(
    () => ensureRechnungPaymentColumn(getVisibleDocumentColumns(settings, isMobile), lockedTyp),
    [settings, isMobile, lockedTyp],
  );
  const pinnedColumns = useMemo<DocumentColumnKey[] | undefined>(
    () => (lockedTyp === "rechnung" ? ["zahlung_erfasst"] : undefined),
    [lockedTyp],
  );

  const visibleWidths = useMemo(() => {
    const raw = getVisibleDocumentWidths(settings, isMobile);
    const out: Record<string, number> = {};
    for (const [key, value] of Object.entries(raw)) {
      const num = Number(value);
      if (Number.isFinite(num) && num > 0) out[key] = clampDocumentColumnWidth(num);
    }
    return out;
  }, [settings, isMobile]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (widthSaveTimerRef.current) {
        window.clearTimeout(widthSaveTimerRef.current);
        widthSaveTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const id = searchParams.get("id");
    if (id && /^\d+$/.test(id)) {
      openDocumentEditor({ docId: Number(id) });
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("id");
          return next;
        },
        { replace: true },
      );
    }
  }, [searchParams, openDocumentEditor, setSearchParams]);

  const requestParams = useMemo(
    () => ({
      // Mahnungen laufen über eine eigene Liste, nicht über die Dokument-API.
      typ: filters.typ && filters.typ !== "mahnung" ? filters.typ : undefined,
      status: filters.status || undefined,
      q: filters.q.trim() || undefined,
      datum_von: filters.datumVon || undefined,
      datum_bis: filters.datumBis || undefined,
      auftrag_id: searchParams.get("auftrag_id")
        ? Number(searchParams.get("auftrag_id"))
        : undefined,
      sort: sort?.key,
      direction: sort?.direction,
    }),
    [filters, searchParams, sort],
  );

  const loadFirstPage = useCallback(async (opts?: { silent?: boolean }) => {
    if (filters.typ === "mahnung") {
      setItems([]);
      setTotalCount(null);
      setHasMore(false);
      setLoading(false);
      setError(null);
      return;
    }
    const silent = Boolean(opts?.silent);
    const cacheKey = JSON.stringify(requestParams);
    const seeded = _dokumenteFirstPageCache.get(cacheKey);
    if (seeded && (!silent || itemsLengthRef.current === 0)) {
      setItems(seeded.items);
      setTotalCount(seeded.totalCount);
      setHasMore(
        seeded.items.length >= PAGE_SIZE &&
          (seeded.totalCount == null || seeded.items.length < seeded.totalCount),
      );
      hasLoadedOnceRef.current = true;
      setLoading(false);
    } else if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      // Stilles Refresh: bereits geladene Seiten behalten (kein Sprung auf Seite 0).
      const loaded = itemsLengthRef.current;
      const limit =
        silent && loaded > PAGE_SIZE ? Math.min(Math.max(loaded, PAGE_SIZE), 500) : PAGE_SIZE;
      const { items: list, totalCount: total } = await listDokumenteWithTotal({
        ...requestParams,
        limit,
        offset: 0,
      });
      if (!isMountedRef.current) return;
      _dokumenteFirstPageCache.set(cacheKey, { items: list, totalCount: total });
      setItems(list);
      setTotalCount(total);
      hasLoadedOnceRef.current = true;
      setHasMore(list.length >= PAGE_SIZE && (total == null || list.length < total));
    } catch (err) {
      if (!isMountedRef.current) return;
      if (!silent) {
        setError(err instanceof ApiError ? err.message : "Liste konnte nicht geladen werden.");
        if (!seeded) {
          setItems([]);
          setTotalCount(null);
          setHasMore(false);
        }
      }
    } finally {
      if (isMountedRef.current && !silent) setLoading(false);
    }
  }, [filters.typ, requestParams]);

  const loadMore = useCallback(async () => {
    if (loading || loadingMore || !hasMore || filters.typ === "mahnung") return;
    setLoadingMore(true);
    try {
      const { items: list, totalCount: total } = await listDokumenteWithTotal({
        ...requestParams,
        limit: PAGE_SIZE,
        offset: items.length,
      });
      if (!isMountedRef.current) return;
      const nextLength = items.length + list.length;
      setItems((prev) => [...prev, ...list]);
      if (total != null) setTotalCount(total);
      setHasMore(list.length >= PAGE_SIZE && (total == null || nextLength < total));
    } catch (err) {
      console.warn("[Dokumente] loadMore failed:", err);
    } finally {
      if (isMountedRef.current) setLoadingMore(false);
    }
  }, [filters.typ, loading, loadingMore, hasMore, items.length, requestParams]);

  useEffect(() => {
    // Filter/Suche: bestehende Zeilen behalten (kein Spinner-/Layout-Zucken).
    void loadFirstPage({ silent: hasLoadedOnceRef.current });
  }, [loadFirstPage]);

  const loadNachfass = useCallback(async (opts?: { silent?: boolean }) => {
    if (!showAngebotNachfass) {
      setNachfassGroups([]);
      setNachfassFollowedGroups([]);
      setNachfassPendingGroups([]);
      setNachfassGroup(null);
      return;
    }
    const silent = Boolean(opts?.silent);
    if (!silent) setNachfassLoading(true);
    try {
      const data = await listAngeboteNachfass();
      if (!isMountedRef.current) return;
      const nextGroups = Array.isArray(data.items) ? data.items : [];
      const nextFollowed = Array.isArray(data.followed_up_items) ? data.followed_up_items : [];
      const nextPending = Array.isArray(data.pending_items) ? data.pending_items : [];
      setNachfassGroups(nextGroups);
      setNachfassFollowedGroups(nextFollowed);
      setNachfassPendingGroups(nextPending);
      setNachfassGroup((prev) => {
        if (!prev) return null;
        return (
          [...nextGroups, ...nextFollowed, ...nextPending].find((g) => g.key === prev.key) ?? null
        );
      });
    } catch (err) {
      console.warn("[Dokumente] Nachfass-Übersicht fehlgeschlagen:", err);
      if (!isMountedRef.current) return;
      if (!silent) {
        setNachfassGroups([]);
        setNachfassFollowedGroups([]);
        setNachfassPendingGroups([]);
      }
    } finally {
      if (isMountedRef.current && !silent) setNachfassLoading(false);
    }
  }, [showAngebotNachfass]);

  useEffect(() => {
    void loadNachfass();
  }, [loadNachfass]);

  const refreshList = useCallback(() => {
    void loadFirstPage({ silent: true });
    void loadNachfass({ silent: true });
  }, [loadFirstPage, loadNachfass]);

  // Realtime-Liste still aktualisieren (inkl. hero_changed für Hero-Kundendokumente).
  // Nur während offenem Editor pausieren – sonst würde Autosave die Liste zucken lassen.
  // useRealtimeSync holt ausstehende Events (auch Hero) beim Schließen einmal nach.
  useRealtimeSync({
    domains: ["dokumente", "timesheets", "hero"],
    extraScopes: ["dokumente:list", "hero:list"],
    extraEvents: ["dokument_status_changed", "timesheets_changed", "hero_changed"],
    debounceMs: 500,
    paused: documentEditorOpen,
    onChange: refreshList,
  });

  async function handleRowOpen(row: DokumentListItem) {
    if (isStundennachweisListItem(row)) {
      const timesheetId = row.timesheet_id ?? (row.id < 0 ? -row.id : row.id);
      if (timesheetId > 0) {
        openTimesheetDetail(timesheetId);
      }
      return;
    }
    if (isStuecklisteListItem(row)) {
      const auftragId = row.auftrag_id;
      const stuecklisteId = row.stueckliste_id;
      if (auftragId && auftragId > 0) {
        openProjectOverview(auftragId, {
          openMontageunterlagen: true,
          stuecklisteId: stuecklisteId && stuecklisteId > 0 ? stuecklisteId : undefined,
        });
      }
      return;
    }
    if (isHeroCustomerDocumentListItem(row)) {
      const heroId = row.hero_document_id;
      if (!heroId || heroId <= 0 || heroOpenBusyId != null) return;
      setHeroOpenBusyId(heroId);
      try {
        const activated = await activateHeroDokument(heroId);
        openDocumentEditor({
          docId: activated.id,
          onClosed: () => {
            void loadFirstPage();
          },
        });
        void loadFirstPage();
      } catch (err) {
        toast.error(
          err instanceof ApiError ? err.message : "Hero-Dokument konnte nicht geöffnet werden.",
        );
      } finally {
        setHeroOpenBusyId(null);
      }
      return;
    }
    openDocumentEditor({
      docId: row.id,
      onClosed: () => {
        refreshList();
      },
    });
  }

  async function handleCapturePayment(row: DokumentListItem) {
    if (!canOpenDokumentZahlungen(row) || paymentBusyId != null) return;
    setPaymentBusyId(row.id);
    try {
      let detail: DokumentDetail;
      if (isHeroCustomerDocumentListItem(row)) {
        const heroId = row.hero_document_id;
        if (!heroId || heroId <= 0) {
          toast.error("Hero-Rechnung ohne ID.");
          return;
        }
        detail = await activateHeroDokument(heroId);
        void loadFirstPage();
      } else {
        detail = await getDokument(row.id);
      }
      if (!canOpenDokumentZahlungen(detail)) {
        toast.error("Zahlungen sind für diesen Status nicht möglich.");
        void loadFirstPage();
        return;
      }
      setPaymentsDoc(detail);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Rechnung konnte nicht geladen werden.");
    } finally {
      setPaymentBusyId(null);
    }
  }

  function updateFilters(patch: Partial<DocumentFilters>) {
    setFilters((prev) => ({
      ...prev,
      ...patch,
      ...(lockedTyp ? { typ: lockedTyp } : {}),
    }));
  }

  function resetFilters() {
    setFilters({ ...DEFAULT_FILTERS, typ: lockedTyp ?? "" });
    if (searchParams.get("status")) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("status");
          return next;
        },
        { replace: true },
      );
    }
  }

  const handleColumnWidthChange = useCallback(
    (key: string, width: number) => {
      const clamped = clampDocumentColumnWidth(width);
      setSettings((prev) => {
        const viewKey = isMobile ? "mobile" : "desktop";
        const currentView = prev[viewKey];
        const next = {
          ...prev,
          [viewKey]: {
            columns: currentView?.columns?.length ? currentView.columns : visibleColumns,
            widths: { ...(currentView?.widths ?? {}), [key]: clamped },
          },
        };
        pendingSettingsRef.current = next;
        return next;
      });

      if (widthSaveTimerRef.current) {
        window.clearTimeout(widthSaveTimerRef.current);
      }
      widthSaveTimerRef.current = window.setTimeout(() => {
        const payload = pendingSettingsRef.current;
        pendingSettingsRef.current = null;
        widthSaveTimerRef.current = null;
        if (!payload) return;
        void saveDocumentColumnSettings(payload).catch((err) => {
          console.warn("[Dokumente] saveColumnWidths failed:", err);
        });
      }, 500);
    },
    [isMobile, setSettings, visibleColumns],
  );

  const countLabel =
    loading && items.length === 0
      ? "· Lade …"
      : formatDocumentCountLabel(totalCount ?? items.length);
  const lockedPage = lockedTyp ? LOCKED_PAGE[lockedTyp] : null;
  const pageTitle = lockedPage?.title ?? "Dokumente";
  const pageSubtitle = lockedPage
    ? lockedPage.subtitle
    : "Angebote, Rechnungen, Lieferscheine, Auftragsbestätigungen und Stundennachweise";
  const createLabel = lockedPage?.createLabel ?? "Neues Dokument";
  const showOverviewActions = !userIsBuero(user) && !lockedTyp;
  const showAngebotPanels = showAngebotNachfass && (!lockedTyp || lockedTyp === "angebot");
  const filtersAreActive = documentHasActiveFilters(filters, { ignoreTyp: Boolean(lockedTyp) });

  return (
    <AppShell
      title={pageTitle}
      actions={
        <>
          {showOverviewActions ? (
            <>
              <Link
                to="/dokumente/mahnwesen"
                className="inline-flex items-center gap-2 rounded-lg border border-[var(--eh-color-border)] px-3 py-2 text-sm font-medium text-label hover:bg-[var(--eh-color-surface-muted)]"
              >
                Mahnwesen
                <NavBadge badgeKey="mahnwesen-faellig" count={badgeCounts["mahnwesen-faellig"] ?? 0} />
              </Link>
              <Link
                to="/dokumente/einstellungen"
                className="inline-flex items-center rounded-lg border border-[var(--eh-color-border)] px-3 py-2 text-sm font-medium text-label hover:bg-[var(--eh-color-surface-muted)]"
              >
                Einstellungen
              </Link>
            </>
          ) : null}
          <Button
            type="button"
            data-tour="dokument-neu"
            onClick={() => openDocumentCreate(lockedTyp ? { typ: lockedTyp } : undefined)}
          >
            {createLabel}
          </Button>
        </>
      }
    >
      <div className="space-y-4 md:space-y-5">
        <section className="rounded-ios-lg bg-card px-4 py-4 shadow-ios-md ring-1 ring-separator/35 dark:ring-separator/50 md:px-5 md:py-4">
          <div className="flex flex-col gap-3 md:gap-4 lg:flex-row lg:items-center lg:justify-between lg:gap-5">
            <div className="min-w-0 flex-1 space-y-1 lg:max-w-[min(100%,40rem)]">
              <h2 className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-lg font-semibold leading-snug tracking-tight text-label md:text-xl">
                <span>{lockedPage?.title ?? "Geschäftsdokumente"}</span>
                <span
                  className="shrink-0 whitespace-nowrap text-sm font-medium tabular-nums text-label-secondary md:text-[15px]"
                  aria-live="polite"
                >
                  {countLabel}
                </span>
              </h2>
              <p className="text-xs text-label-secondary md:text-sm">
                {pageSubtitle}
              </p>
            </div>
            <div className="flex min-w-0 shrink-0 flex-col gap-2 sm:flex-row sm:items-center sm:gap-2 lg:max-w-full xl:min-w-[min(100%,36rem)] xl:flex-1 xl:justify-end">
              <SearchInput
                value={filters.q}
                onChange={(q) => updateFilters({ q })}
                placeholder="Dokumente durchsuchen: Angebotsnummer, Kunde, Projekt …"
                className="w-full min-w-0 flex-1 sm:min-w-[12rem]"
              />
              <div className="flex shrink-0 flex-wrap gap-2 sm:justify-end">
                <Button
                  type="button"
                  variant="secondary"
                  className="min-w-0 whitespace-nowrap"
                  onClick={() => setColumnsDialogOpen(true)}
                >
                  Spalten
                </Button>
              </div>
            </div>
          </div>
        </section>

        {showAngebotPanels &&
        (nachfassLoading ||
          nachfassGroups.length > 0 ||
          nachfassFollowedGroups.length > 0 ||
          nachfassPendingGroups.length > 0) ? (
          <CollapsibleSection
            sectionKey="dokumente.angebot-kacheln"
            title="Angebote nachfassen"
            summary={
              [
                nachfassGroups.length > 0
                  ? `${nachfassGroups.length} Ansprechpartner > 2 Wochen ohne Rückmeldung`
                  : null,
                nachfassFollowedGroups.length > 0
                  ? `${nachfassFollowedGroups.length} erneut versendet`
                  : null,
                nachfassGroups.length === 0 && nachfassPendingGroups.length > 0
                  ? `${nachfassPendingGroups.length} offen, noch keine zwei Wochen`
                  : null,
              ]
                .filter(Boolean)
                .join(" · ") || undefined
            }
          >
            <AngebotAgingPanels
              groups={nachfassGroups}
              followedUpGroups={nachfassFollowedGroups}
              pendingGroups={nachfassPendingGroups}
              loading={nachfassLoading}
              onOpenGroup={setNachfassGroup}
            />
          </CollapsibleSection>
        ) : null}

        <CollapsibleSection
          sectionKey="dokumente.filter"
          title="Filter"
          summary={filtersAreActive ? "Filter aktiv" : "Alle Dokumente"}
          actions={
            filtersAreActive ? (
              <button
                type="button"
                onClick={() => resetFilters()}
                className="rounded-lg px-2 py-1 text-xs font-medium text-accent transition hover:bg-fill-tertiary"
              >
                Zurücksetzen
              </button>
            ) : null
          }
        >
          <DocumentFilterBar
            filters={filters}
            onChange={updateFilters}
            onReset={resetFilters}
            hideTyp={Boolean(lockedTyp)}
          />
        </CollapsibleSection>

        {error ? (
          <Alert variant="error" title="Liste konnte nicht geladen werden">
            {error}
          </Alert>
        ) : null}

        <section>
          {filters.typ === "mahnung" ? null : (
            <TableSortResetBar visible={sort != null} onReset={resetSort} />
          )}
          {filters.typ === "mahnung" ? (
            <MahnungDokumenteList
              q={filters.q}
              status={filters.status}
              datumVon={filters.datumVon}
              datumBis={filters.datumBis}
            />
          ) : loading && items.length === 0 ? (
            <div className="flex items-center justify-center py-10 text-label-secondary">
              <Spinner />
              <span className="ml-2 text-sm">Dokumente werden geladen …</span>
            </div>
          ) : (
            <DocumentTable
              rows={items}
              columns={visibleColumns}
              widths={visibleWidths}
              loading={loading}
              loadingMore={loadingMore}
              hasMore={hasMore}
              onLoadMore={() => void loadMore()}
              onRowOpen={handleRowOpen}
              onCapturePayment={(row) => void handleCapturePayment(row)}
              paymentBusyId={paymentBusyId}
              onColumnWidthChange={handleColumnWidthChange}
              sort={sort}
              onSort={toggle}
            />
          )}
          {filters.typ !== "mahnung" && !loading && items.length === 0 && !error && filtersAreActive ? (
            <div className="mt-4 flex flex-col items-center gap-3 rounded-ios bg-fill-quaternary/25 px-4 py-4 text-center shadow-ios ring-1 ring-separator/30 dark:ring-separator/45">
              <p className="max-w-md text-sm text-label-secondary">
                Es wurden keine Dokumente gefunden. Alle Filter zurücksetzen, um die gesamte Liste zu sehen.
              </p>
              <Button type="button" variant="secondary" size="sm" onClick={resetFilters}>
                Filter zurücksetzen
              </Button>
            </div>
          ) : null}
        </section>
      </div>

      <DocumentColumnsDialog
        open={columnsDialogOpen}
        onClose={() => setColumnsDialogOpen(false)}
        settings={settings}
        pinnedColumns={pinnedColumns}
        onSave={async (next) => {
          await saveSettings(next);
        }}
      />

      {paymentsDoc ? (
        <DocumentPaymentsDialog
          open
          doc={paymentsDoc}
          onClose={() => setPaymentsDoc(null)}
          onUpdated={(next) => {
            setPaymentsDoc(next);
            void loadFirstPage();
          }}
        />
      ) : null}

      <AngebotNachfassModal
        open={nachfassGroup != null}
        group={nachfassGroup}
        onClose={() => setNachfassGroup(null)}
        onChanged={async () => {
          await Promise.all([loadNachfass(), loadFirstPage()]);
        }}
        onOpenDokument={(id) => {
          setNachfassGroup(null);
          openDocumentEditor({
            docId: id,
            onClosed: () => {
              void loadNachfass();
              void loadFirstPage();
            },
          });
        }}
      />
    </AppShell>
  );
}
